import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { createHandymanSocialSharing } from '../../util/handymanSocialSharing';
import { buildHandymanLocalBusinessSchema } from '../../util/handymanLocalBusinessSchema';
import { propTypes } from '../../util/types';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import HandymanListingsSection from './HandymanListingsSection';
import { createHandymanMapStateToProps } from './handymanPageState';
import { OPEN_QUOTE_EVENT } from '../../components/QuoteChat/QuoteChat';
import css from './HandymanPage.module.css';
import { handymanCityLinks } from '../../data/handymanSeoPages';

const HERO_IMAGE =
  '/static/services/handyman/handyman-hero-banner-painting-and-furniture.jpg';

// Open the AI intake chat, optionally seeding the first message.
const openQuoteChat = prefill => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_QUOTE_EVENT, { detail: prefill ? { prefill } : {} }));
  }
};

const contentSections = [
  {
    title: 'What handyman jobs look like in Ontario',
    paragraphs: [
      'Most requests are a short list of practical tasks: TV mounting, shelf installation, curtain rods, blinds, drywall patching, mirror hanging, light fixture swaps, caulking, and small door or hardware repairs. These are jobs where it matters to get it right — level, clean, without damage to the wall.',
      'Many people have three or four tasks piling up at once. A handyman can often work through the full list in one visit, which is usually faster and better value than booking each job separately.',
    ],
  },
  {
    title: 'Who ends up booking handyman help',
    paragraphs: [
      'Homeowners with a to-do list that keeps getting delayed. Tenants setting up a new place who need blinds, curtain rods, and a TV mounted before it feels liveable. Landlords with a turnover punch list before a new tenant moves in.',
      'Small offices also use handyman services for shelving, wall mounting, and hardware updates — work that needs doing but does not justify calling a full contractor.',
    ],
  },
  {
    title: 'How to book a handyman in Ontario',
    paragraphs: [
      'Browse listings, check recent reviews and pricing, then message the provider directly. You agree on scope and timing before anything is confirmed. No middlemen — you talk to the person doing the work.',
      'Be specific in your message: what needs mounting, what the walls are made of, whether you have more than one task. The more detail you give, the faster you get a useful reply.',
    ],
  },
  {
    title: 'Choosing the right provider',
    paragraphs: [
      "Look at recent reviews for jobs similar to yours. A provider who does a lot of condo TV mounting in concrete approaches it differently than someone who mostly works in detached homes. Match the experience to the job.",
      "If you're not sure, just describe your space in the first message. A good handyman will tell you whether it's their kind of work.",
    ],
  },
];

const faqs = [
  {
    question: 'What kinds of handyman jobs can I find on FixitTask?',
    answer:
      'Common jobs include TV mounting, shelf installation, curtain rods, blinds, drywall patching, mirror hanging, light fixture swaps, caulking, door adjustments, and other smaller repair or installation tasks around the home or office.',
  },
  {
    question: 'How much does a handyman cost in Ontario?',
    answer:
      'Prices vary by job and provider. Simple tasks like picture hanging or a curtain rod can be done in under an hour. More involved work — concrete wall anchors, multi-task visits, drywall patching — costs more. Browse individual listings to see current pricing.',
  },
  {
    question: 'Can a handyman handle several tasks in one visit?',
    answer:
      "Yes, and it's usually the better approach. List everything when you message — TV, shelves, curtain rods, whatever. Most providers can work through a short list in one appointment, which is more efficient and often cheaper per task than booking separately.",
  },
  {
    question: 'Do I need to be home during the visit?',
    answer:
      "Yes, for most jobs. You'll need to let the provider in and confirm placement details — how high to mount the TV, where exactly the shelves go. Plan to be present for the full visit.",
  },
];

export const HandymanPageComponent = props => {
  const { listings, queryInProgress, queryListingsError } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';

  const createSearchPath = params =>
    createResourceLocatorString('SearchPage', routeConfiguration, {}, params);

  const browseAllPath = createSearchPath({ pub_categoryLevel1: 'Furniture_Assembly_Installation' });

  const quotePrefill = 'I need a handyman in Ontario';

  // No live listings yet → invite into the chat instead of a dead-end.
  const listingsEmptyState = (
    <div className={css.listingsState}>
      <h3 className={css.cardTitle}>Tell us the job — we’ll find you a handyman</h3>
      <p className={css.cardText}>
        New pros join every week. Describe what needs doing (a photo helps) and we’ll match you
        with a trusted local handyman and call you back shortly.
      </p>
      <button type="button" className={css.quoteButton} onClick={() => openQuoteChat(quotePrefill)}>
        Get a free quote
      </button>
    </div>
  );

  const title = 'Handyman Services in Ontario | Repairs & Mounting | FixitTask';
  const description =
    'Find local handymen across Ontario on FixitTask. Compare live listings, reviews, and prices for repairs, mounting, patching, and small home jobs.';

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Handyman Services in Ontario',
      description,
      url: `${rootUrl}/handyman`,
    },
    buildHandymanLocalBusinessSchema({
      rootUrl,
      pageUrl: `${rootUrl}/handyman`,
      name: 'Handyman Services in Ontario',
      description,
      image: `${rootUrl}${HERO_IMAGE}`,
      cities: handymanCityLinks.map(item => item.city),
      region: 'Ontario',
    }),
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Handyman',
          item: `${rootUrl}/handyman`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <Page
      title={title}
      description={description}
      socialSharing={createHandymanSocialSharing(rootUrl, title, description)}
      schema={schema}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}/handyman`} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero} style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
            <div className={css.heroOverlay}>
              <div className={css.heroContent}>
                <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                    Home
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <span>Handyman</span>
                </nav>

                <p className={css.eyebrow}>Trusted local help across Ontario</p>
                <h1 className={css.title}>Handyman Services in Ontario</h1>
                <p className={css.lead}>
                  Find local handymen on FixitTask for repairs, installations, mounting, patching,
                  finishing work, and the everyday small jobs that keep homes, rentals, and offices
                  running smoothly.
                </p>

                <div className={css.heroCta}>
                  <button
                    type="button"
                    className={css.quoteButton}
                    onClick={() => openQuoteChat(quotePrefill)}
                  >
                    Get a free quote
                  </button>
                  <ul className={css.trustRow}>
                    <li>Reply in minutes</li>
                    <li>Vetted local pros</li>
                    <li>No sign-up, no obligation</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <HandymanListingsSection
            heading="Find the best handyman near you"
            text="Discover trusted local professionals, compare prices and real reviews, chat directly before booking, and confidently choose the best option for your project."
            listings={listings}
            queryInProgress={queryInProgress}
            queryListingsError={queryListingsError}
            browseAllPath={browseAllPath}
            emptyStateChildren={listingsEmptyState}
          />

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Why customers use handyman services</p>
              <h2 className={css.sectionTitle}>
                Handyman help for repairs, installations, and everyday maintenance
              </h2>
              <p className={css.sectionText}>
                Use this page when you need a practical starting point for finding reliable help
                with smaller jobs across Ontario.
              </p>
            </div>

            <div className={css.contentGrid}>
              {contentSections.map(section => (
                <article className={css.contentCard} key={section.title}>
                  <h3 className={css.contentTitle}>{section.title}</h3>
                  {section.paragraphs.map(paragraph => (
                    <p className={css.contentParagraph} key={paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <div className={css.cityPanel}>
              <div className={css.sectionHeader}>
                <p className={css.sectionEyebrow}>Popular Ontario cities</p>
                <h2 className={css.sectionTitle}>
                  Browse handyman help by city
                </h2>
                <p className={css.sectionText}>
                  Start with a major Ontario city if you already know where the job needs to be
                  done.
                </p>
              </div>

              <div className={css.cityGrid}>
                {handymanCityLinks.map(item => (
                  <a className={css.cityLink} href={item.href} key={item.slug}>
                    <span>{item.city}</span>
                    <span className={css.cityAction}>Browse</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>FAQ</p>
              <h2 className={css.sectionTitle}>Questions customers usually have</h2>
            </div>

            <div className={css.faqGrid}>
              {faqs.map(item => (
                <article className={css.faqCard} key={item.question}>
                  <h3 className={css.cardTitle}>{item.question}</h3>
                  <p className={css.cardText}>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

HandymanPageComponent.defaultProps = {
  listings: [],
  queryListingsError: null,
};

const { arrayOf, bool } = PropTypes;

HandymanPageComponent.propTypes = {
  listings: arrayOf(propTypes.listing),
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
};

const HandymanPage = compose(
  connect(createHandymanMapStateToProps('HandymanPage'))
)(HandymanPageComponent);

export default HandymanPage;
